import { HttpClient } from '@angular/common/http';
import { Injectable, WritableSignal, signal } from '@angular/core';
import { Subject, lastValueFrom } from 'rxjs';
import { Node, buildRootNode } from '../components/explorer/node';
import { UserSettingsService } from './user-settings.service';
import { QueueService } from './queue.service';

export type ItemType = 'file' | 'folder' | null;

@Injectable({
  providedIn: 'root',
})
export class FileSystemService {
  constructor(
    private userSettings: UserSettingsService,
    private http: HttpClient,
    private queue: QueueService
  ) {
    // AI-related subscriptions moved to AiFileWriterService
  }
  tabs: WritableSignal<Node[]> = signal([]);

  visibleNode: WritableSignal<Node> = signal(
    new Node('/workspace/README.txt', true, [])
  );
  lastSelectedNode: WritableSignal<Node> = signal(
    new Node('/workspace/README.txt', true, [])
  );
  creatingNewItem: WritableSignal<ItemType> = signal(null);
  public rootNode!: Node;

  private rootNodeRefreshSubject = new Subject<Node>();

  get rootNodeRefresh$() {
    return this.rootNodeRefreshSubject.asObservable();
  }

  getFileType(path: string): 'code' | 'md' | 'data' | 'image' | 'yaml' | 'json' | 'unknown' {
    const extension = path.split('.').pop()?.toLowerCase() || '';

    // Code file extensions
    const codeExtensions = [
      'js',
      'ts',
      'html',
      'css',
      'scss',
      'py',
      'java',
      'c',
      'cpp',
      'cs',
      'go',
      'rb',
      'php',
      'r',
      'swift',
      'txt',
      // SAS programs and the log/listing the SAS runner writes beside them.
      'sas',
      'log',
      'lst',
    ];
    // Data file extensions
    const dataExtensions = ['csv', 'xpt', 'sas7bdat', '.sav', '.dta']; //'tsv', 'xlsx', 'xls', 'xml', 'json'];
    // Image file extensions
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'svg', 'bmp', 'webp'];
    // YAML file extensions
    const yamlExtensions = ['yaml', 'yml'];

    if (codeExtensions.includes(extension)) return 'code';
    if (extension === 'md') return 'md';
    if (dataExtensions.includes(extension)) return 'data';
    if (imageExtensions.includes(extension)) return 'image';
    if (yamlExtensions.includes(extension)) return 'yaml';
    if (extension === 'json') return 'json';

    return 'unknown';
  }

  async createItem(path: string, isFolder: boolean, content: string | null) {
    try {
      const newNode = this.rootNode.insert(path, isFolder, content);
      this.queue.add({ type: 'createFile', filename: path, content: content || '', output: `Created ${isFolder ? 'folder' : 'file'}: ${path}` });
      return newNode;
    } catch (error) {
      this.queue.add({ type: 'error', content: `Error creating item at ${path}: ${error instanceof Error ? error.message : String(error)}` });
      return null;
    }
  }

  async deleteItem(node: Node) {
    if (node.isFolder) {
      this.rootNode.delete(node.path);
      await this.deleteFromUser(node);
      this.visibleNode.set(this.rootNode);
    }
    else {
      this.rootNode.delete(node.path);
      await this.deleteFromUser(node);
      this.tabs.update((tabs) => tabs.filter((tab) => tab.path !== node.path));
      // If the deleted file was the visible node, show another tab
      if (this.visibleNode() === node) {
        const remainingTabs = this.tabs();
        if (remainingTabs.length > 0) {
          this.visibleNode.set(remainingTabs[0]);
        }
      }
    }
  }

  public async changeVisibleNode(path: string, create: boolean = false) {
    let node = this.rootNode.getNode(path);
    if (!node && !create) {
      throw new Error(`File not found: ${path}`);
    }
    if (!node && create) {
      node = await this.createItem(path, false, null);
    }
    if (!node) {
      // This should never happen, but added for type safety
      console.error(`Failed to get or create node for path: ${path}`);
      return false;
    } else {
      // Check if a tab with the same path already exists
      let existingTab = this.tabs().find(tab => tab.path === node.path);
      if (!existingTab) {
        // Add only if not present
        this.tabs.update(tabs => [...tabs, node]);
        existingTab = node;
      }
      this.visibleNode.set(existingTab);
      return true;
    }
  }

  public async updateVisibleNode(content: string) {
    const node = this.visibleNode();
    this.rootNode.update(node.path, node.name, node.isFolder, content);

    this.visibleNode.update((prevNode) => {
      // Return a brand-new Node object
      return new Node(
        prevNode.path,
        prevNode.isFolder,
        prevNode.items,
        content
      );
    });
  }

  async userContentChange(content: string) {
    this.updateVisibleNode(content);
  }

  public async refreshRootNode() {
    // Logic to refresh the rootNode
    // this.rootNode = await this.rootNode.fetch();
    if (!this.userSettings.serverUrl) {
      throw new Error('Server URL is not set, but it is required to refresh tree', {
        cause: { code: 'SERVER_URL_NOT_SET' },
      });
    }
    this.rootNode = await buildRootNode(this.userSettings.serverUrl);
    this.rootNodeRefreshSubject.next(this.rootNode);
  }

  async removeVisibleNode(value: Node) {
    this.tabs.update((tabs) =>
      tabs.filter((tab) => tab !== value)
    );
    if (this.visibleNode() === value) {
      const remainingTabs = [...this.tabs()];
      if (remainingTabs.length > 0) {
        this.changeVisibleNode(remainingTabs[0].path);
        this.visibleNode.set(remainingTabs[0]);
      }
    }
  }

  async uploadFiles(files: File[], parentNode: Node): Promise<void> {
    // const parentNode = this.lastSelectedNode();
    const path = parentNode.path.split('/').slice(2).join('/');
    const formData = new FormData();

    // Append each file with the correct field name 'files'
    for (const file of files) {
      formData.append('files', file);
    }
    formData.append('path', path);
    await lastValueFrom(this.http.post(`${this.userSettings.serverUrl}/upload-file`, formData));
  }

  async deleteFromUser(node: Node) {
    const path = node.path.split('/').slice(2).join('/');
    return await lastValueFrom(this.http.post(`${this.userSettings.serverUrl}/delete?path=${path}`, {}));
  }
}
